import { useEffect, useState } from 'react';
import { getCurrentRound, getRoster } from '../core/api';
import { useT } from '../core/lexicon';
import { useAsync } from '../core/useAsync';
import { Avatar } from '../core/sprite';
import { useApp } from '../App';
import { Screen, TopBar, CenterNote } from './Layout';
import type { CurrentRound, PlayerProgress, RosterMember } from '../core/types';

const COLS = [
  { key: 'points_done', label: 'progress.col.points' },
  { key: 'veto_done', label: 'progress.col.veto' },
  { key: 'guesses_done', label: 'progress.col.guess' },
] as const;

/**
 * Who's done what this round — every player against the three voting acts (points,
 * veto, line-up). Shows the ACTS, never their content (same secrecy stance as the
 * server): a ✓ once a part is drafted, a dot until then. Style-agnostic; the words
 * come from the lexicon and the palette re-tints per theme.
 */
export function VoteProgress() {
  const t = useT();
  const { user } = useApp();
  const round = useAsync<CurrentRound | null>(getCurrentRound, []);
  const [roster, setRoster] = useState<Map<number, RosterMember>>(new Map());
  useEffect(() => {
    getRoster()
      .then((l) => setRoster(new Map(l.map((m) => [m.user_id, m]))))
      .catch(() => {});
  }, []);

  const r = round.data ?? null;
  const rows: PlayerProgress[] = r?.progress ?? [];

  return (
    <Screen>
      <TopBar title={t('progress.name')} sub={r ? `${r.ballots_in}/${r.expected_players}` : undefined} />
      {round.loading ? (
        <CenterNote>
          <div className="spin">LOADING…</div>
        </CenterNote>
      ) : !r ? (
        <CenterNote title={t('empty.no_round').toUpperCase()} />
      ) : (
        <div className="progrid">
          <div className="phead">
            <span />
            {COLS.map((c) => (
              <span key={c.key}>{t(c.label).toUpperCase()}</span>
            ))}
          </div>
          {rows.map((p) => (
            <div key={p.user_id} className={`prow${p.user_id === user.user_id ? ' you' : ''}`}>
              <span className="pwho">
                <Avatar
                  spriteData={roster.get(p.user_id)?.sprite_data ?? null}
                  seed={roster.get(p.user_id)?.sprite_key || String(p.user_id)}
                  size={28}
                />
                <span className="nm">{p.display_name.toUpperCase()}</span>
              </span>
              {COLS.map((c) => (
                <span key={c.key} className={`pcell${p[c.key] ? ' on' : ''}`} aria-label={p[c.key] ? 'done' : 'not yet'}>
                  {p[c.key] ? '✓' : '·'}
                </span>
              ))}
            </div>
          ))}
        </div>
      )}
    </Screen>
  );
}
